import { noop } from 'lodash-es';
import type { ReactNode } from 'react';
import React, { Component } from 'react';

import { fjModalSizes } from 'components/primitives';
import { Modal } from 'components/styled-primitives';

interface ButtonConfig {
  /**
   * Button text
   */
  text: string;
  /**
   * Callback after action (and closing modal)
   */
  callback: () => void;
  /**
   * Button is disabled?
   */
  disabled: boolean;
}

interface GlobalConfirmationState {
  open: boolean;
  /**
   * Text to display at top of modal
   */
  heading: string;
  /**
   * Optional body content
   */
  body: ReactNode;
  /**
   * Modal size
   */
  size: string;
  cancel: ButtonConfig;
  accept: ButtonConfig;
}

interface ConfirmConfig {
  heading?: string;
  body?: ReactNode;
  cancel?: Partial<ButtonConfig>;
  accept?: Partial<ButtonConfig>;
}

class GlobalConfirmation extends Component<Record<string, never>, GlobalConfirmationState> {
  constructor(props: Record<string, never>) {
    super(props);
    this.state = this.getDefaultState();
  }

  getDefaultState = (): GlobalConfirmationState => ({
    open: false,
    /**
     * Text to display at top of modal
     */
    heading: '',
    /**
     * Optional body content
     */
    body: null,
    /**
     * Modal size
     */
    size: 'sm',
    cancel: {
      /**
       * Cancel button text
       */
      text: 'Cancel',
      /**
       * Callback after canceling (and closing modal)
       */
      callback: noop,
      /**
       * Cancel is disabled?
       */
      disabled: false,
    },
    accept: {
      /**
       * Confirmation button text
       */
      text: 'OK',
      /**
       * Callback after accepting (and closing modal)
       */
      callback: noop,
      /**
       * Confirmation is disabled?
       */
      disabled: false,
    },
  });

  // Public API: consuming app should set a ref on this component and call `thatRef.confirm` with
  // config for header and buttons
  confirm = (config: ConfirmConfig = {}): void => {
    this.setState((state) => ({
      open: true,
      heading: config.heading || '',
      body: config.body || null,
      cancel: {
        ...state.cancel,
        ...(config.cancel || {}),
      },
      accept: {
        ...state.accept,
        ...(config.accept || {}),
      },
    }));
  };

  cancel = (): void => {
    const next = this.state.cancel.callback;
    this.setState(this.getDefaultState(), next);
  };

  accept = (): void => {
    const next = this.state.accept.callback;
    this.setState(this.getDefaultState(), next);
  };

  render(): ReactNode {
    if (!this.state.open) {
      return null;
    }

    return (
      <Modal
        cancel={{
          disabled: this.state.cancel.disabled,
          text: this.state.cancel.text,
          callback: this.cancel,
        }}
        accept={{
          disabled: this.state.accept.disabled,
          text: this.state.accept.text,
          callback: this.accept,
        }}
        title={this.state.heading}
        centerContent={!this.state.body}
        size={fjModalSizes[!this.state.body ? 'XS' : 'SM']}
      >
        {this.state.body}
      </Modal>
    );
  }
}

export default GlobalConfirmation;
